import type {Postscriptum} from "../dist/d.ts/postscriptum";
import type {Paginator} from "../dist/d.ts/pages";
import type {Areas, LayoutContext, SubFragmentor, SubProcessorContext} from "../dist/d.ts/layout";
import type {FragmentorEvents, FragmentorOptions} from "../dist/d.ts/fragments";
import type {Process} from "../dist/d.ts/process";
import type {SubProcessorResult} from "../dist/d.ts/layout.js";
import type {Fragmentor} from "../dist/d.ts/fragments";
import type {Point} from "../dist/d.ts/util/ranges";
import type {EventEmitter} from "../dist/d.ts/util/events";

/**
 * Le cas d'usage par défaut est réduire la police jusqu'à trouver une solution valide
 * 'normal' comme valeur de layout pour indiquer un test sans layout mais avec critère
 * 'none' autorisé que comme valeur seul ou en fin : si en fin, il n'y a pas de test de validité au niveau de l'élément
 * la valeur 'auto' pour criteria indique "selected-before max-content min-height / no-inline-overflow" excépté si ce de layout est 'normal' dans ce cas, il correspond à 'none'
 */

(() => {
	const {css, layout, util, mutations, fragments} = postscriptum;
	const {walk, logger, rects} = util;

	type LayoutResult = {
		layout: string;
		score: any;
		step?: number;
		animProp?: string;
	}
	type ValidFlags = ('none' | 'no-inline-overflow')[];

	type StepStops = 'first-valid' | 'first-invalid';

	interface CritContext {
		layoutBefore?: string;
		stepBefore?: number;
		animBefore?: string;
	}

	interface SelectLayoutOptions {
		criteria?: Record<string, Criterion<any>>
	}

	interface Criterion<T> {
		testLayout(subProcessor: TestLayoutProcessor, ctx: CritContext): T;

		compareResult(resultA: T, resultB: T): number;
	}

	abstract class NumberCriterion implements Criterion<number> {
		abstract testLayout(subProcessor: TestLayoutProcessor, ctx: CritContext): number;

		compareResult(resultA: number, resultB: number): number {
			if (resultA == Infinity) return -1;
			else if (resultB == Infinity) return 1;
			else return resultA - resultB;
		}
	}

	interface TestLayoutProcessEvents extends FragmentorEvents {
		'inline-overflow': Record<string, never>;
	}

	class TestLayoutProcessor extends fragments.Fragmentor<FragmentorOptions, TestLayoutProcessEvents> implements SubFragmentor {
		pseudoFragments: HTMLElement[] = [];
		layoutContexts: LayoutContext[] = [];
		testMutations = new mutations.Mutations();

		constructor(source: HTMLElement, readonly parentFragmentor: Fragmentor, readonly pseudoFragmentsCount: number, readonly captureInlineOverflow: boolean) {
			super(source, parentFragmentor.gcpmContext, parentFragmentor.countersContext, {logLevel: parentFragmentor.options.logLevel});

			this.avoidBreakTypes = parentFragmentor.avoidBreakTypes;
			this.logNoValidBreakPoint = false;
			this.startOnRoot = true;
			this.subProcessors = Array.from(parentFragmentor.subProcessors);
			this.parentEmitter = parentFragmentor as EventEmitter<Partial<TestLayoutProcessEvents>>;
			this._process = this.testProcess();
		}

		* testProcess(): Process {
			const parentCtx = this.parentFragmentor.layoutContext;
			const logLevel = this.options.logLevel;
			logger.level = 'error';

			let inlineOverflow = false;
			if (this.captureInlineOverflow) {
				this.on("contents-end", function (this: Fragmentor) {
					const currentBody = this.currentFragment?.body || this.source;
					if (!inlineOverflow && currentBody.scrollWidth > currentBody.clientWidth) inlineOverflow = true;
				}, {inherited: true});
			}

			let remaining = this.source;
			let firstLastEvent = null;
			while (!this.lastEvent || this.lastEvent == "valid-break-point") {
				if (this.lastEvent == "valid-break-point") {
					const lastBody = remaining;
					remaining = this.testMutations.breaks(remaining, this.layoutContext.breakPoint);
					lastBody.after(remaining);
					const bodyBottom = this.layoutContext.bodyBottom;
					if (bodyBottom != Infinity) this.testMutations.setStyle(lastBody, { height: (bodyBottom - rects.boundingScrollRect(lastBody).top) + 'px' });
				}
				this.pseudoFragments.push(remaining);
				yield* this.contentsProcess(remaining, parentCtx.areas, parentCtx);
				this.layoutContext.mutations.commit(this.testMutations);
				this.layoutContexts.push(this.layoutContext);
				if (!firstLastEvent) firstLastEvent = this.lastEvent;
				if (this.pseudoFragments.length == this.pseudoFragmentsCount) break;
			}

			logger.level = logLevel;

			this.layoutContext = this.layoutContexts[0];
			if (inlineOverflow) this.lastEvent = "inline-overflow";
			else this.lastEvent = firstLastEvent;
		}

		createLayoutContext(root: HTMLElement, areas: Areas, parentCtx: LayoutContext): LayoutContext {
			const processor = this as TestLayoutProcessor;

			class ElementLayoutContext extends layout.LayoutContext {
				get bodyBottom(): number {
					const {length: fragmentsCount} = processor.pseudoFragments;
					return fragmentsCount == 1 || fragmentsCount < processor.pseudoFragmentsCount ? this.parentCtx.bodyBottom : Infinity;
				}

				set bodyBottom(val) {
					parentCtx.bodyBottom = val;
				}
			}

			return new ElementLayoutContext(root, parentCtx.body, areas, parentCtx);
		}
	}

	(postscriptum as Postscriptum).plugin<SelectLayoutOptions>('ps-select-layout', (processor: Paginator, options?: SelectLayoutOptions) => {
		CSS.registerProperty({
			name: '--psp-sl-layouts',
			syntax: '*',
			initialValue: 'normal',
			inherits: false
		});

		CSS.registerProperty({
			name: '--psp-sl-criteria',
			syntax: '*',
			initialValue: 'valid',
			inherits: false
		});

		CSS.registerProperty({
			name: '--psp-sl-steps',
			syntax: '*',
			initialValue: 'none',
			inherits: false
		});

		CSS.registerProperty({
			name: '--psp-sl-pseudo-fragments',
			syntax: '<integer>',
			initialValue: '2',
			inherits: true
		});

		const criteriaRegistry: Record<string, Criterion<any>> = {};

		function registerCriterion<T>(code: string, crit: Criterion<T>): void {
			criteriaRegistry[code] = crit;
		}

		registerCriterion('valid', new class extends NumberCriterion {
			testLayout(): number {
				return Infinity;
			}
		});

		registerCriterion('selected-before', new class extends NumberCriterion {
			testLayout(subProcessor: TestLayoutProcessor, ctx: CritContext): number {
				const layout = subProcessor.source.getAttribute('psp-sl-layout');
				const stepAttr = subProcessor.source.getAttribute('psp-sl-step');
				const step = stepAttr ? parseInt(stepAttr) : undefined;
				if (ctx.layoutBefore == layout && ctx.stepBefore == step) return Infinity;
				return -Infinity;
			}
		});

		registerCriterion('min-height', new class extends NumberCriterion {
			testLayout(subProcessor: TestLayoutProcessor): number {
				if (subProcessor.pseudoFragments.length == 1) return -subProcessor.source.clientHeight;
				else {
					const range = subProcessor.doc.createRange();
					range.setStartBefore(subProcessor.pseudoFragments[0]);
					range.setEndAfter(subProcessor.pseudoFragments.at(-1));
					return -range.getBoundingClientRect().height;
				}
			}
		});

		registerCriterion('max-height', new class extends NumberCriterion {
			testLayout(subProcessor: TestLayoutProcessor): number {
				if (subProcessor.pseudoFragments.length == 1) return subProcessor.source.clientHeight;
				else {
					const range = subProcessor.doc.createRange();
					range.setStartBefore(subProcessor.pseudoFragments[0]);
					range.setEndAfter(subProcessor.pseudoFragments.at(-1));
					return range.getBoundingClientRect().height;
				}
			}
		});

		registerCriterion('max-content-on-break', {
			testLayout(subProcessor: TestLayoutProcessor): Point {
				const firstLayoutCtx = subProcessor.layoutContexts[0];
				const {breakPoint} = firstLayoutCtx;
				return breakPoint;
			},
			compareResult(resultA: Point, resultB: Point): number {
				const doc = resultA.container.ownerDocument;
				const rangeA = doc.createRange();
				rangeA.setStart(resultA.container, resultA.offset);
				const rangeB = doc.createRange();
				rangeB.setStart(resultB.container, resultB.offset);
				return rangeA.compareBoundaryPoints(Range.START_TO_START, rangeB);
			}
		});

		if (options?.criteria) for (const criterionCode in options.criteria) {
			registerCriterion(criterionCode, options.criteria[criterionCode]);
		}

		const subProcessor = function (this: Fragmentor, ctx: SubProcessorContext): SubProcessorResult {
			const {currentElement: elem, currentStyle: style} = ctx;
			if (!walk.firstStaticChild(elem)) return;
			// The subprocessor is not called again when testing this element or if a parent has a selected layout (solutions for his descendants have been already selected).
			if (elem.hasAttribute('psp-sl-layout-test') || elem.closest('psp-sl-layout:not([psp-sl-layout-test])')) return false;

			const layoutsProp = css.getCustomPluginProp(style, 'sl-layouts');
			const criteriaProp = css.getCustomPluginProp(style, 'sl-criteria');
			const pseudoFragmentsCount = parseInt(css.getCustomPluginProp(style, 'sl-pseudo-fragments'));
			if (layoutsProp == 'normal' && criteriaProp == 'valid') return;

			return (async () => {
				elem.setAttribute('psp-sl-layout-test', '');
				const stylesheet = css.insertStyleSheet(processor.doc, `ps-page { overflow: visible }`, true, 'ps-select-layout');
				const layoutsList = layoutsProp.split(/\s*,\s*/).map((list) => list.split(/\s+/));
				const validFlagsList: ValidFlags[] = [];
				const criteriaList = criteriaProp.split(/\s*,\s*/).map((list) => {
					const [criteria, validFlags] = list.split(/\s*\/\s*/);
					validFlagsList.push(validFlags ? validFlags.split(/\s+/) as ValidFlags : []);
					return criteria.split(/\s+/);
				});

				const critCtx: CritContext = {};
				if (elem.hasAttribute('ps-breaked-before')) {
					if (elem.hasAttribute('psp-sl-layout')) critCtx.layoutBefore = elem.getAttribute('psp-sl-layout');
					if (elem.hasAttribute('psp-sl-step')) critCtx.stepBefore = parseInt(elem.getAttribute('psp-sl-step'));
					if (elem.style.animation) critCtx.animBefore = elem.style.animation;
					elem.removeAttribute('psp-sl-layout');
					elem.removeAttribute('psp-sl-step');
					elem.style.animation = '';
				}


				let bestResult: LayoutResult;
				const testSubFragmentor = async (validFlags: ValidFlags, validCb: (subProcessor: TestLayoutProcessor) => void): Promise<boolean> => {
					const noInlineOverflow = validFlags && validFlags.includes('no-inline-overflow');
					const subProcessor = new TestLayoutProcessor(elem, this, pseudoFragmentsCount, noInlineOverflow);
					await subProcessor.start().ended;

					const {lastEvent} = subProcessor;
					const valid = lastEvent == 'valid-break-point' || lastEvent == 'no-overflow';
					if (valid) validCb(subProcessor);
					subProcessor.testMutations.revert();
					return valid;
				};

				for (let i = 0; i < layoutsList.length && !bestResult; i++) {
					const layouts = layoutsList[i];
					let criteria = criteriaList[i];
					let validFlags = validFlagsList[i];
					if (!criteria) criteria = criteriaList.at(-1);
					if (!validFlags) validFlags = validFlagsList.at(-1) || ['none'];

					if (criteria[0] == 'selected-before') {
						if (layouts.includes(critCtx.layoutBefore)) {
							if (critCtx.stepBefore) {
								const anim = elem.getAnimations().at(-1);
								anim.currentTime = critCtx.stepBefore * 1000;
							}
							await testSubFragmentor(validFlags, () => bestResult = {
								layout: critCtx.layoutBefore,
								animProp: critCtx.animBefore,
								step: critCtx.stepBefore,
								score: Infinity
							});
							if (bestResult) {
								logger.info(`Layout selected before: ${bestResult.layout}${bestResult.step ? '.' + bestResult.step : ''} on ${elem.className}`, 'ps-select-layout');
								break;
							}
						}
						criteria.shift();
					}

					const results: LayoutResult[][] = Array.from({length: criteria.length}, () => []);
					for (const layoutCode of layouts) {
						const testMuts = new mutations.Mutations();
						testMuts.setAttr(elem, 'psp-sl-layout', layoutCode);
						const stepsProp = css.getCustomPluginProp(elem, 'sl-steps');
						let steps = -1;
						let stepStop: StepStops;
						let animProp: string;
						let anim: Animation;
						if (stepsProp != "none") {
							const stepParts = stepsProp.split(/\s*\/\s*/);
							if (stepParts.length > 1) stepStop = stepParts[1] as StepStops;
							const [keyframes, stepsCount, timing] = stepParts[0].split(' ');
							animProp = `${stepsCount}s ${timing || 'linear'} paused ${keyframes}`;
							testMuts.setStyle(elem, {animation: animProp});
							anim = elem.getAnimations().at(-1);
							if (!anim) logger.warn(`Unable to define the steps '${stepsProp}'. Is the corresponding keyframes at-rule missing ?`);
							else steps = parseInt(stepsCount);
						}

						if (steps && anim) {
							for (let step = 0; step < steps; step++) {
								anim.currentTime = step * 1000;
								const valid = await testSubFragmentor(validFlags, (subProcessor: TestLayoutProcessor) => {
									for (let j = 0; j < criteria.length; j++) {
										const critCode = criteria[j];
										const criterion = criteriaRegistry[critCode];
										if (criterion) {
											const score = criterion.testLayout(subProcessor, critCtx);
											results[j].push({layout: layoutCode, animProp, step, score});
										} else {
											logger.warn(`Unknown criterion '${critCode}'`);
										}
									}
								});
								if (stepStop) {
									if (valid && stepStop == 'first-valid') break;
									else if (!valid && stepStop == 'first-invalid') break;
								}
							}
						} else {
							await testSubFragmentor(validFlags, (subProcessor: TestLayoutProcessor) => {
								for (let j = 0; j < criteria.length; j++) {
									const critCode = criteria[j];
									const criterion = criteriaRegistry[critCode];
									if (criterion) {
										const score = criterion.testLayout(subProcessor, critCtx);
										results[j].push({layout: layoutCode, score});
									} else {
										logger.warn(`Unknown criterion '${critCode}'`);
									}
								}
							});
						}

						testMuts.revert();
					}

					let selectedLayouts = Array.from(layouts);
					for (let iCrit=0; iCrit<criteria.length; iCrit++) {
						const critResults = results[iCrit];
						let selectedResults: LayoutResult[] = [];
						for (const critResult of critResults) {
							if (selectedLayouts.includes(critResult.layout)) {
								const criterion = criteriaRegistry[criteria[iCrit]];
								if (!selectedResults.length || criterion.compareResult(critResult.score, selectedResults[0].score) > 0) selectedResults = [critResult];
								else if (criterion.compareResult(critResult.score, selectedResults[0].score) == 0) selectedResults.push(critResult);
							}
						}
						bestResult = selectedResults[0];
						if (selectedResults.length > 1) selectedLayouts = selectedResults.map((result) => result.layout);
						else break;
					}
				}

				elem.removeAttribute('psp-sl-layout-test');
				stylesheet.ownerNode.remove();
				if (bestResult) {
					elem.setAttribute('psp-sl-layout', bestResult.layout);
					if (bestResult.step !== undefined) {
						const {mutations: boxMutations} = ctx.currentBox;
						boxMutations.setStyle(elem, {'animation': bestResult.animProp});
						const anim = elem.getAnimations().at(-1);
						anim.currentTime = bestResult.step * 1000;
						boxMutations.setAnimations(elem);
						boxMutations.setAttr(elem, 'psp-sl-step', bestResult.step.toString());
					}
					logger.info(`Layout selected: ${bestResult.layout}${bestResult.step !== undefined ? '.' + bestResult.step : ''} on ${elem.className}`, 'ps-select-layout');
					return false;
				} else {
					elem.removeAttribute('psp-sl-layout');
					elem.style.animation = '';
					return {event: 'no-break-point' as const};
				}
			})();
		};
		processor.prependSubProcessor(subProcessor);
	}, {criteria: {}});
})();